import type { Point2 } from './lib'

// ---------------------------------------------------------------------------
// Medição sobre o desenho 2D.
//
// As coordenadas do desenho já estão em METROS no plano da vista (ver
// `viewTypes`), por isso medir é geometria elementar: comprimento
// acumulado da polilinha, área por fórmula do sapateiro (shoelace) e
// centróide do polígono.
//
// Ressalva honesta sobre a área: mede-se o polígono que o utilizador
// clicou, não a projecção de nada. Num CORTE, uma superfície inclinada
// aparece encurtada no desenho e a área medida é a área PROJECTADA, não
// a real. Numa planta horizontal as duas coincidem.
//
// Puro: sem DOM, sem React.
// ---------------------------------------------------------------------------

/** Comprimento de uma polilinha. `closed` acrescenta o troço de fecho. */
export function pathLength(pts: Point2[], closed = false): number {
  if (!pts || pts.length < 2) return 0
  let total = 0
  for (let i = 0; i + 1 < pts.length; i++) {
    total += Math.hypot(pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1])
  }
  if (closed && pts.length > 2) {
    const a = pts[pts.length - 1]
    const b = pts[0]
    total += Math.hypot(b[0] - a[0], b[1] - a[1])
  }
  return total
}

/** Área do polígono (fórmula do sapateiro), sempre positiva. O sentido
 *  de percurso não interessa a quem mede. Menos de 3 pontos = 0. */
export function polygonArea(pts: Point2[]): number {
  if (!pts || pts.length < 3) return 0
  let twice = 0
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]
    const b = pts[(i + 1) % pts.length]
    twice += a[0] * b[1] - b[0] * a[1]
  }
  return Math.abs(twice) / 2
}

/** Centróide do polígono. Degenera para a média aritmética dos pontos
 *  quando a área é nula (pontos colineares ou repetidos), que é o que
 *  se quer para lá pôr a etiqueta. */
export function polygonCentroid(pts: Point2[]): Point2 {
  if (!pts || pts.length === 0) return [0, 0]
  if (pts.length === 1) return [pts[0][0], pts[0][1]]
  if (pts.length === 2) {
    return [(pts[0][0] + pts[1][0]) / 2, (pts[0][1] + pts[1][1]) / 2]
  }
  let twice = 0
  let cx = 0
  let cy = 0
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]
    const b = pts[(i + 1) % pts.length]
    const cross = a[0] * b[1] - b[0] * a[1]
    twice += cross
    cx += (a[0] + b[0]) * cross
    cy += (a[1] + b[1]) * cross
  }
  if (Math.abs(twice) < 1e-12) {
    let sx = 0
    let sy = 0
    for (const p of pts) {
      sx += p[0]
      sy += p[1]
    }
    return [sx / pts.length, sy / pts.length]
  }
  return [cx / (3 * twice), cy / (3 * twice)]
}

export interface MeasureResult {
  /** 'linear' = dois pontos; 'path' = polilinha aberta com 3+ pontos;
   *  'area' = polígono fechado. */
  kind: 'linear' | 'path' | 'area'
  points: Point2[]
  /** Comprimento acumulado (perímetro, quando fechado). */
  length: number
  /** Só em 'area'. */
  area: number
  /** Onde pôr a etiqueta. */
  centroid: Point2
}

/** Resultado de uma medição. `closed` só tem efeito com 3+ pontos: dois
 *  pontos são sempre uma distância. */
export function measure(pts: Point2[], closed: boolean): MeasureResult | null {
  if (!pts || pts.length < 2) return null
  const clean = pts.filter(
    (p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]),
  )
  if (clean.length < 2) return null
  const isArea = closed && clean.length >= 3
  const kind: MeasureResult['kind'] =
    clean.length === 2 ? 'linear' : isArea ? 'area' : 'path'
  return {
    kind,
    points: clean,
    length: pathLength(clean, isArea),
    area: isArea ? polygonArea(clean) : 0,
    centroid: polygonCentroid(clean),
  }
}

/** Número em PT-PT: vírgula decimal, sem separador de milhares (num
 *  desenho o separador de milhares só confunde com a vírgula). */
export function fmtPt(v: number, decimals = 2): string {
  if (!Number.isFinite(v)) return '-'
  return v.toFixed(decimals).replace('.', ',')
}

/** Etiqueta curta do resultado, pronta a virar anotação.
 *  Área: "A = 12,34 m²". Distância: "8,00 m". */
export function measureLabel(r: MeasureResult | null): string {
  if (!r) return ''
  if (r.kind === 'area') return `A = ${fmtPt(r.area)} m²`
  return `${fmtPt(r.length)} m`
}

/** Linha de estado da barra: o que se mostra enquanto se mede e no
 *  fim. */
export function measureSummary(r: MeasureResult | null): string {
  if (!r) return ''
  if (r.kind === 'area') {
    return `A = ${fmtPt(r.area)} m² · perímetro ${fmtPt(r.length)} m`
  }
  if (r.kind === 'linear') return `Distância ${fmtPt(r.length)} m`
  return `Comprimento ${fmtPt(r.length)} m`
}
